import React from 'react';
import { cn } from '@sandtech-hub-monorepo/ui-kit';

import { ReactComponent as HeroConfetti } from '../assets/gsa/frontier-confetti.svg';

interface Props {
  backgroundImage: string;
  children: React.ReactNode;
  className?: string;
  /** The Path Chooser banner's decorative FIT shapes, top-right corner. Opt-in — not every hero has one. */
  showDecorations?: boolean;
  /** Overrides the default confetti shapes when `showDecorations` is set, for a page with its own. */
  decoration?: React.ReactNode;
}

/**
 * Dark "Deep Space Blue" hero banner used across the GSA pages
 * (path chooser, catalogue). Renders a cover image behind a navy overlay,
 * matching the Figma banner.
 */
export const PageHero = ({
  backgroundImage,
  children,
  className,
  showDecorations = false,
  decoration,
}: Props) => (
  <div className={cn('relative overflow-hidden bg-heading', className)}>
    <img
      src={backgroundImage}
      alt=""
      className={cn(
        // `object-top` (0%) gave the subjects' heads no margin at all - any
        // shrink of this padding-driven box (more text wrap, a wider/shorter
        // viewport) cropped straight into a forehead with nothing to spare.
        // A little headroom above the anchor point survives that shrink
        // without pushing the crop noticeably off "top-anchored".
        // Hidden below `sm` regardless, leaving the container's flat
        // `bg-heading` as a solid banner - a narrow crop of any of these
        // photos reads worse than no photo at all.
        'absolute inset-y-0 right-0 hidden h-full w-3/4 object-cover object-[center_15%] sm:block'
      )}
    />
    {/* Solid navy on the left fading to transparent by ~65% width - a soft
        blend into the photo rather than a flat scrim or a visible crop
        seam. */}
    <div
      className="absolute inset-0 hidden sm:block"
      style={{
        backgroundImage:
          'linear-gradient(90deg, hsl(var(--heading)) 25%, hsl(var(--heading) / 0) 65%)',
      }}
    />
    {showDecorations &&
      (decoration ?? (
        <HeroConfetti
          aria-hidden
          className="pointer-events-none absolute right-0 top-0 hidden h-full w-auto sm:block"
        />
      ))}
    <div className="relative mx-auto w-full max-w-screen-2xl px-6 sm:px-8 md:px-[108px] py-14 md:py-[91px] text-white">
      {children}
    </div>
  </div>
);
